import React, { useState, useEffect } from 'react';
import { X, Search, Users, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose
} from '../ui/dialog';

const getInitials = (name = '') =>
  name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

const roleConfig = {
  admin:      { label: 'Admin',      className: 'bg-red-100 text-red-700' },
  instructor: { label: 'Instructor', className: 'bg-blue-100 text-blue-700' },
  student:    { label: 'Student',    className: 'bg-emerald-100 text-emerald-700' },
};

const NewConversationModal = ({
  isOpen,
  onClose,
  onCreateConversation,
  currentUser,
  availableUsers = []
}) => {
  const [conversationName, setConversationName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      setConversationName('');
      setSelectedUsers([]);
      setSearchTerm('');
    }
  }, [isOpen]);

  const filteredUsers = availableUsers.filter(user =>
    `${user.name} ${user.lastName} ${user.email}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isSelected = (user) => selectedUsers.some(u => u._id === user._id);

  const toggleUser = (user) => {
    setSelectedUsers(prev =>
      isSelected(user) ? prev.filter(u => u._id !== user._id) : [...prev, user]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!conversationName.trim() || selectedUsers.length === 0) return;
    onCreateConversation({
      name: conversationName.trim(),
      participants: selectedUsers.filter(u => u._id !== currentUser.id),
      type: 'custom'
    });
    onClose();
  };

  const canSubmit = conversationName.trim() && selectedUsers.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="flex flex-col p-0 gap-0 w-full max-w-md mx-4">
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>New Conversation</DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Add a name and select participants
              </p>
            </div>
            <DialogClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 -mr-1">
                <X className="w-4 h-4" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 140px)' }}>
          <form id="new-conv-form" onSubmit={handleSubmit}>

            {/* Conversation Name */}
            <div className="px-5 py-4">
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Conversation Name
              </label>
              <Input
                value={conversationName}
                onChange={e => setConversationName(e.target.value)}
                placeholder="e.g. Study Group, Project Team…"
                maxLength={100}
                required
                className="h-10"
                autoFocus
              />
            </div>

            <Separator />

            {/* Selected chips */}
            {selectedUsers.length > 0 && (
              <>
                <div className="px-5 py-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Participants ({selectedUsers.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedUsers.map(user => (
                      <div
                        key={user._id}
                        className="flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-medium px-2.5 py-1 rounded-full"
                      >
                        <span>{user.name} {user.lastName}</span>
                        <button
                          type="button"
                          onClick={() => toggleUser(user)}
                          className="hover:text-destructive transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* Search */}
            <div className="px-5 py-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Add People
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search by name or email…"
                  className="pl-9 h-9 bg-muted border-0 focus-visible:ring-1"
                />
              </div>
            </div>

            {/* User list */}
            <div className="max-h-52 overflow-y-auto">
              <div className="px-3 pb-3 space-y-0.5">
                {filteredUsers.length === 0 ? (
                  <div className="flex flex-col items-center py-10 text-muted-foreground">
                    <Users className="w-8 h-8 mb-2 opacity-30" />
                    <p className="text-sm">No users found</p>
                  </div>
                ) : (
                  filteredUsers.map(user => {
                    const selected = isSelected(user);
                    const role = roleConfig[user.role];
                    return (
                      <button
                        key={user._id}
                        type="button"
                        onClick={() => toggleUser(user)}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-150',
                          selected ? 'bg-primary/10' : 'hover:bg-muted'
                        )}
                      >
                        <Avatar className="w-9 h-9 flex-shrink-0">
                          {(user.profilePicture?.url || user.profilePicture?.data) && (
                            <AvatarImage
                              src={user.profilePicture.url || `data:${user.profilePicture.contentType};base64,${user.profilePicture.data}`}
                              alt={user.name}
                            />
                          )}
                          <AvatarFallback className="text-xs font-semibold bg-primary/10 text-primary">
                            {getInitials(`${user.name} ${user.lastName}`)}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">
                            {user.name} {user.lastName}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        </div>

                        {role && (
                          <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0', role.className)}>
                            {role.label}
                          </span>
                        )}

                        <div className={cn(
                          'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all',
                          selected ? 'bg-primary border-primary' : 'border-border'
                        )}>
                          {selected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="new-conv-form"
            className="flex-1"
            disabled={!canSubmit}
          >
            Create
            {selectedUsers.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 px-1.5 py-0 text-[10px] h-4">
                {selectedUsers.length}
              </Badge>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewConversationModal;
